import * as React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { makeStyles } from '@material-ui/core/styles';
import Check from '@material-ui/icons/Check';
import clsx from 'clsx';
import { selectOption as coreSelectOption } from '@configuratorware/configurator-frontendgui/App/Reducers/Configurator/Actions';
import {
  getOptionClassificationById,
  getOptionClassificationTitle,
  getOptionsForClassification,
  getSelectedOptionForClassification,
  OptionClassificationIdentifiers,
} from '../../Reducers/Configurator/Selectors';
import { XXL_COLORS } from '../../theme/colors';
import SectionTitle from './SectionTitle';
import { resolveMountingSelectionAmount } from './mountingTypeSelection';

const useStyles = makeStyles((theme) => {
  const tokens = (theme as { xxlprint?: typeof XXL_COLORS }).xxlprint || XXL_COLORS;
  return {
    section: {
      marginBottom: theme.spacing(2),
    },
    list: {},
    row: {
      display: 'flex',
      alignItems: 'center',
      gap: theme.spacing(1.25),
      padding: theme.spacing(0.75, 0),
      cursor: 'pointer',
      userSelect: 'none',
      '&:first-child': { paddingTop: 0 },
    },
    rowSelected: {},
    checkbox: {
      flexShrink: 0,
      width: 15,
      height: 15,
      border: `1.5px solid ${tokens.borderMedium}`,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      backgroundColor: tokens.paperBackground,
      transition: 'background-color 0.1s ease, border-color 0.1s ease',
    },
    checkboxSelected: {
      backgroundColor: tokens.accent,
      borderColor: tokens.accent,
    },
    checkIcon: {
      fontSize: 11,
      color: '#ffffff',
      pointerEvents: 'none',
    },
    label: {
      flex: 1,
      fontSize: 13,
      color: tokens.sectionTitle,
      lineHeight: 1.4,
    },
    labelSelected: {
      fontWeight: 600,
    },
  };
});

type MountingOption = {
  identifier: string;
  title: string;
};

export default function MountingTypeSection() {
  const classes = useStyles();
  const dispatch = useDispatch();
  const options = useSelector(getOptionsForClassification(OptionClassificationIdentifiers.mountingType)) as MountingOption[];
  const selected = useSelector(getSelectedOptionForClassification(OptionClassificationIdentifiers.mountingType));
  const title = useSelector(getOptionClassificationTitle(OptionClassificationIdentifiers.mountingType));
  const optionClassification = useSelector(getOptionClassificationById(OptionClassificationIdentifiers.mountingType));
  const requestInFlight = React.useRef(false);

  // Auto-select when only one option is available
  React.useEffect(() => {
    if (options.length === 1 && !selected?.identifier && optionClassification) {
      dispatch(coreSelectOption({ ...options[0], amount: 1 }, true, false, optionClassification));
    }
  }, [options.length, !!selected?.identifier]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!options.length) return null;

  // Ignore clicks while a switch request is in flight
  const handleSelect = (option: MountingOption) => {
    if (!optionClassification || requestInFlight.current) return;
    requestInFlight.current = true;
    const amount = resolveMountingSelectionAmount(selected?.identifier, option.identifier);
    Promise.resolve(
      dispatch(coreSelectOption({ ...option, amount }, true, false, optionClassification)) as unknown as Promise<unknown>,
    ).finally(() => {
      requestInFlight.current = false;
    });
  };

  return (
    <div className={classes.section}>
      <SectionTitle>{title}</SectionTitle>
      <div className={classes.list} role="radiogroup" aria-label={title}>
        {options.map((option) => {
          const isSelected = selected?.identifier === option.identifier;
          return (
            <div
              key={option.identifier}
              className={clsx(classes.row, isSelected && classes.rowSelected)}
              onClick={() => handleSelect(option)}
              role="radio"
              aria-checked={isSelected}
              tabIndex={0}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  handleSelect(option);
                }
              }}
            >
              <div className={clsx(classes.checkbox, isSelected && classes.checkboxSelected)} aria-hidden>
                {isSelected && <Check className={classes.checkIcon} />}
              </div>
              <span className={clsx(classes.label, isSelected && classes.labelSelected)}>
                {option.title}
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
}
